import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./NewEmpLeave.css";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const ALLOWED_MODULES = [1, 2, 3, 7, 13, 16, 29];
const MAX_DOC_KB = 200;

function compressImage(imageFile, maxKB) {
  return new Promise((resolve) => {
    const maxBytes = maxKB * 1024;
    if (imageFile.size <= maxBytes) { resolve(imageFile); return; }
    const img = new Image();
    const url = URL.createObjectURL(imageFile);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      canvas.getContext("2d").drawImage(img, 0, 0);
      let quality = 0.85;
      const tryNext = () => {
        canvas.toBlob((blob) => {
          if (!blob) { resolve(imageFile); return; }
          if (blob.size <= maxBytes || quality <= 0.1) {
            resolve(new File([blob], imageFile.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" }));
          } else {
            quality = Math.max(0.1, quality - 0.1);
            tryNext();
          }
        }, "image/jpeg", quality);
      };
      tryNext();
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(imageFile); };
    img.src = url;
  });
}

const getToday = () => new Date().toISOString().slice(0, 10);

const getNoOfDays = (fromDate, toDate, hfDay) => {
  if (!fromDate || !toDate) {
    return "";
  }

  const start = new Date(fromDate);
  const end = new Date(toDate);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
    return "";
  }

  const oneDay = 1000 * 60 * 60 * 24;
  const diffDays = Math.floor((end - start) / oneDay) + 1;

  if (hfDay === "F" || hfDay === "S") {
    return diffDays === 1 ? "0.5" : `${diffDays - 0.5}`;
  }

  return String(diffDays);
};

export default function NewEmpLeave() {
  const localUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const moduleId = Number.parseInt(localUser?.moduleId, 10);
  const employeeId = localUser?.id;
  const departmentId = localUser?.deptid;

  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState({
    LeaveTypeID: "",
    FromDate: getToday(),
    ToDate: "",
    NoOfDays: "",
    HFDay: "W",
    Remarks: "",
    SupportingDoc: null
  });

  useEffect(() => {
    if (!ALLOWED_MODULES.includes(moduleId)) {
      setError("Unauthorized access Or Session Timed out.");
      return;
    }

    const fetchLeaveTypes = async () => {
      try {
        setLoadingTypes(true);
        const res = await fetch(`${API_ROOT}/api/employees/leave-types?moduleId=${moduleId}`);
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to fetch leave types");
        }

        setLeaveTypes(Array.isArray(data.data) ? data.data : []);
      } catch (err) {
        setError(err.message || "Failed to fetch leave types");
      } finally {
        setLoadingTypes(false);
      }
    };

    fetchLeaveTypes();
  }, [moduleId]);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      NoOfDays: getNoOfDays(prev.FromDate, prev.ToDate, prev.HFDay)
    }));
  }, [formData.FromDate, formData.ToDate, formData.HFDay]);

  const onTextChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setFormData(prev => ({ ...prev, SupportingDoc: null }));
      return;
    }

    const allowed = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
    if (!allowed.includes(file.type)) {
      setError("Only PDF, JPG, JPEG, PNG files are allowed.");
      e.target.value = "";
      setFormData(prev => ({ ...prev, SupportingDoc: null }));
      return;
    }

    let uploadFile = file;
    if (file.type.startsWith("image/") && file.size > MAX_DOC_KB * 1024) {
      uploadFile = await compressImage(file, MAX_DOC_KB);
    }

    if (uploadFile.size > MAX_DOC_KB * 1024) {
      setError(`Supporting document must be less than ${MAX_DOC_KB} KB`);
      e.target.value = "";
      setFormData(prev => ({ ...prev, SupportingDoc: null }));
      return;
    }

    setError("");
    setFormData(prev => ({ ...prev, SupportingDoc: uploadFile }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!employeeId || !departmentId) {
      setError("Session data missing. Please login again.");
      return;
    }

    if (!formData.LeaveTypeID || !formData.FromDate || !formData.ToDate || !formData.NoOfDays) {
      setError("Required fields are missing.");
      return;
    }

    if (new Date(formData.FromDate) > new Date(formData.ToDate)) {
      setError("Invalid date range: From Date cannot be after To Date.");
      return;
    }

    const payload = new FormData();
    payload.append("EmployeeID", String(employeeId));
    payload.append("DepartmentID", String(departmentId));
    payload.append("LeaveTypeID", String(formData.LeaveTypeID));
    payload.append("FromDate", formData.FromDate);
    payload.append("ToDate", formData.ToDate);
    payload.append("NoOfDays", String(formData.NoOfDays));
    payload.append("HFDay", formData.HFDay);
    payload.append("Remarks", formData.Remarks || "");

    if (formData.SupportingDoc) {
      payload.append("SupportingDoc", formData.SupportingDoc);
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${API_ROOT}/api/employees/leave-apply`, {
        method: "POST",
        body: payload
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Failed to apply leave");
      }

      setSuccess(data.message || "Leave successfully added.");
      setFormData({
        LeaveTypeID: "",
        FromDate: getToday(),
        ToDate: "",
        NoOfDays: "",
        HFDay: "W",
        Remarks: "",
        SupportingDoc: null
      });
    } catch (err) {
      setError(err.message || "Failed to apply leave");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="leave-form-wrap">
      <div className="leave-top-links">
        <Link to="/LeaveDetails" className="btn btn-primary btn-sm">Leave Details</Link>
      </div>

      <h3 className="leave-form-title">New Leave (Self)</h3>

      {error && <div className="alert alert-danger">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form className="leave-form-grid" onSubmit={onSubmit} encType="multipart/form-data">
        <div>
          <label htmlFor="LeaveTypeID" className="form-label">Type of Leave</label>
          <select
            className="form-control"
            name="LeaveTypeID"
            id="LeaveTypeID"
            value={formData.LeaveTypeID}
            onChange={onTextChange}
            disabled={loadingTypes}
            required
          >
            <option value="">Select Leave Type</option>
            {leaveTypes.map(item => (
              <option key={item.LeaveTypeID} value={item.LeaveTypeID}>{item.LeaveType}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="FromDate" className="form-label">From Date</label>
          <input
            className="form-control"
            name="FromDate"
            id="FromDate"
            type="date"
            value={formData.FromDate}
            onChange={onTextChange}
            required
          />
        </div>

        <div>
          <label htmlFor="ToDate" className="form-label">To Date</label>
          <input
            className="form-control"
            name="ToDate"
            id="ToDate"
            type="date"
            value={formData.ToDate}
            onChange={onTextChange}
            required
          />
        </div>

        <div>
          <label htmlFor="NoOfDays" className="form-label">No. of Days</label>
          <input
            className="form-control"
            name="NoOfDays"
            id="NoOfDays"
            type="number"
            step="0.01"
            value={formData.NoOfDays}
            onChange={onTextChange}
            required
          />
        </div>

        <div>
          <label htmlFor="HFDay" className="form-label">Half/Full Day</label>
          <select
            className="form-control"
            name="HFDay"
            id="HFDay"
            value={formData.HFDay}
            onChange={onTextChange}
            required
          >
            <option value="W">Full Day</option>
            <option value="F">First Half</option>
            <option value="S">Second Half</option>
          </select>
        </div>

        <div className="leave-form-wide">
          <label htmlFor="Remarks" className="form-label">Remarks/Address & Contact for Station Leave</label>
          <input
            className="form-control"
            name="Remarks"
            id="Remarks"
            type="text"
            maxLength={255}
            value={formData.Remarks}
            onChange={onTextChange}
          />
        </div>

        <div className="leave-form-wide">
          <label htmlFor="SupportingDoc" className="form-label">
            Supporting Document (max {MAX_DOC_KB} KB)
            <span
              title={`Allowed: PDF, JPG, JPEG, PNG. Max ${MAX_DOC_KB} KB. Images above ${MAX_DOC_KB} KB are auto-compressed.`}
              style={{ cursor: "help", marginLeft: 6, color: "#0d6efd" }}
            >&#9432;</span>
          </label>
          <input
            className="form-control"
            name="SupportingDoc"
            id="SupportingDoc"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={onFileChange}
          />
        </div>

        <div className="leave-form-wide">
          <button type="submit" name="add" id="add" className="btn btn-primary" disabled={submitting || loadingTypes}>
            {submitting ? "Submitting..." : "Apply Leave"}
          </button>
        </div>
      </form>
    </div>
  );
}
